import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePortalStore } from '../../store';
import { Lock, Eye, VolumeX, Plus, Trash2, Link, BookOpen } from 'lucide-react';
import type { OntologyEntity, OntologyRelationship, FounderControl, EntityType, RelationshipType } from '../../types';

// — constants —

const NODE_W = 148;
const NODE_H = 72;
const CANVAS_W = 900;
const CANVAS_H = 560;
const CX = CANVAS_W / 2;
const CY = CANVAS_H / 2;

const TYPE_COLORS: Record<string, string> = {
  'Competitor':     'border-red-500 bg-red-950',
  'Customer Segment': 'border-blue-500 bg-blue-950',
  'Market':         'border-green-500 bg-green-950',
  'Problem':        'border-orange-500 bg-orange-950',
  'Technology':     'border-purple-500 bg-purple-950',
  'Regulation':     'border-yellow-500 bg-yellow-950',
  'Business Model': 'border-teal-500 bg-teal-950',
  'Resource':       'border-slate-500 bg-slate-800',
  'Partner':        'border-indigo-500 bg-indigo-950',
  'Trend':          'border-pink-500 bg-pink-950',
};

const ENTITY_TYPES: EntityType[] = [
  'Competitor', 'Customer Segment', 'Market', 'Problem',
  'Technology', 'Regulation', 'Business Model', 'Resource', 'Partner', 'Trend',
];
const RELATIONSHIP_TYPES: RelationshipType[] = [
  'competes with', 'serves', 'threatens', 'enables',
  'depends on', 'regulated by', 'experiences pain', 'operates in',
];
const FOUNDATIONAL = new Set(['Problem', 'Customer Segment']);

// — physics —

type Vec = { x: number; y: number; vx: number; vy: number };

function initPositions(entities: OntologyEntity[]): Map<string, Vec> {
  const pos = new Map<string, Vec>();
  const typeCount: Record<string, number> = {};
  const typeAngle: Record<string, number> = {
    'Problem': 0, 'Customer Segment': 60, 'Market': 120,
    'Competitor': 180, 'Technology': 240, 'Regulation': 300,
    'Business Model': 30, 'Partner': 150, 'Resource': 270, 'Trend': 330,
  };
  for (const e of entities) {
    const n = typeCount[e.entity_type] ?? 0;
    typeCount[e.entity_type] = n + 1;
    const baseAngle = ((typeAngle[e.entity_type] ?? 0) + n * 25) * (Math.PI / 180);
    const radius = FOUNDATIONAL.has(e.entity_type) ? 80 + n * 30 : 200 + n * 30;
    pos.set(e.id, {
      x: CX + Math.cos(baseAngle) * radius - NODE_W / 2,
      y: CY + Math.sin(baseAngle) * radius - NODE_H / 2,
      vx: 0,
      vy: 0,
    });
  }
  return pos;
}

function applyRepulsion(pos: Map<string, Vec>, k: number): void {
  const ids = [...pos.keys()];
  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      const a = pos.get(ids[i])!;
      const b = pos.get(ids[j])!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
      const force = k / (dist * dist);
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      a.vx -= fx; a.vy -= fy;
      b.vx += fx; b.vy += fy;
    }
  }
}

function applySprings(pos: Map<string, Vec>, rels: OntologyRelationship[]): void {
  for (const r of rels) {
    const a = pos.get(r.from_entity);
    const b = pos.get(r.to_entity);
    if (!a || !b) continue;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
    const restLen = 200 - r.strength * 100;
    const force = 0.08 * (dist - restLen);
    const fx = (dx / dist) * force;
    const fy = (dy / dist) * force;
    a.vx += fx; a.vy += fy;
    b.vx -= fx; b.vy -= fy;
  }
}

function applyCentering(pos: Map<string, Vec>, entities: OntologyEntity[]): void {
  for (const e of entities) {
    if (!FOUNDATIONAL.has(e.entity_type)) continue;
    const v = pos.get(e.id);
    if (!v) continue;
    v.vx += (CX - v.x) * 0.04;
    v.vy += (CY - v.y) * 0.04;
  }
}

function stepPositions(pos: Map<string, Vec>): void {
  for (const v of pos.values()) {
    v.vx *= 0.85; v.vy *= 0.85;
    v.x = Math.max(0, Math.min(CANVAS_W - NODE_W, v.x + v.vx));
    v.y = Math.max(0, Math.min(CANVAS_H - NODE_H, v.y + v.vy));
  }
}

function runSimulation(
  pos: Map<string, Vec>,
  rels: OntologyRelationship[],
  entities: OntologyEntity[],
  iterations: number,
): void {
  for (let i = 0; i < iterations; i++) {
    applyRepulsion(pos, 6000);
    applySprings(pos, rels);
    applyCentering(pos, entities);
    stepPositions(pos);
  }
}

function useForceSimulation(entities: OntologyEntity[], rels: OntologyRelationship[]) {
  return useMemo(() => {
    const pos = initPositions(entities);
    runSimulation(pos, rels, entities, 200);
    const result = new Map<string, { x: number; y: number }>();
    for (const [id, v] of pos.entries()) {
      result.set(id, { x: Math.round(v.x), y: Math.round(v.y) });
    }
    return result;
  }, [entities, rels]);
}

// — sub-components —

function ControlBadge({ control, onChange }: { control: FounderControl; onChange: (c: FounderControl) => void }) {
  const cycle: FounderControl[] = ['none', 'locked', 'watched', 'muted'];
  const next = () => onChange(cycle[(cycle.indexOf(control) + 1) % cycle.length]);
  if (control === 'locked') return <button onClick={next} title="Locked — click to cycle" className="text-red-400"><Lock className="w-3 h-3" /></button>;
  if (control === 'watched') return <button onClick={next} title="Watched — click to cycle" className="text-yellow-400"><Eye className="w-3 h-3" /></button>;
  if (control === 'muted') return <button onClick={next} title="Muted — click to cycle" className="text-slate-400"><VolumeX className="w-3 h-3" /></button>;
  return <button onClick={next} title="No control — click to set Lock" className="text-slate-600 hover:text-slate-400"><Lock className="w-3 h-3" /></button>;
}

function EntityCardNode({ entity, x, y, selected, onSelect, onControl, onDelete }: {
  entity: OntologyEntity; x: number; y: number; selected: boolean;
  onSelect: () => void; onControl: (c: FounderControl) => void; onDelete: () => void;
}) {
  const colors = TYPE_COLORS[entity.entity_type] ?? 'border-slate-500 bg-slate-800';
  const pendingStyle = entity.is_pending ? 'opacity-50 border-dashed' : '';
  const selectedStyle = selected ? 'ring-2 ring-cyan-400' : '';
  const confPct = Math.round(entity.confidence * 100);

  return (
    <div
      style={{ position: 'absolute', left: x, top: y, width: NODE_W }}
      className={`border rounded-lg p-2 cursor-pointer ${colors} ${pendingStyle} ${selectedStyle} hover:ring-1 hover:ring-slate-400 transition-shadow`}
      onClick={onSelect}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-400 font-medium truncate">{entity.entity_type}</div>
          <div className="text-sm font-semibold text-slate-100 leading-tight truncate mt-0.5">{entity.label}</div>
        </div>
        <ControlBadge control={entity.control} onChange={onControl} />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <div className="flex-1 h-1 bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${confPct}%` }} />
        </div>
        <span className="text-xs text-slate-500 ml-2">{confPct}%</span>
        {entity.is_pending && <span className="text-xs text-amber-400 ml-1">pending</span>}
        <button
          onClick={e => { e.stopPropagation(); onDelete(); }}
          className="ml-1 text-slate-600 hover:text-red-400"
        ><Trash2 className="w-3 h-3" /></button>
      </div>
    </div>
  );
}

function EdgeLayer({ entities, relationships, positions }: {
  entities: OntologyEntity[];
  relationships: OntologyRelationship[];
  positions: Map<string, { x: number; y: number }>;
}) {
  const entityById = useMemo(() => new Map(entities.map(e => [e.id, e])), [entities]);

  return (
    <svg style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} width={CANVAS_W} height={CANVAS_H}>
      <defs>
        <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="#4b5563" />
        </marker>
      </defs>
      {relationships.map(r => {
        const ap = positions.get(r.from_entity);
        const bp = positions.get(r.to_entity);
        if (!ap || !bp) return null;
        const x1 = ap.x + NODE_W / 2;
        const y1 = ap.y + NODE_H / 2;
        const x2 = bp.x + NODE_W / 2;
        const y2 = bp.y + NODE_H / 2;
        const mx = (x1 + x2) / 2;
        const my = (y1 + y2) / 2;
        const fromE = entityById.get(r.from_entity);
        const isPending = fromE?.is_pending;
        return (
          <g key={r.id}>
            <line
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="#4b5563"
              strokeWidth={1 + r.strength}
              strokeOpacity={isPending ? 0.3 : 0.7}
              strokeDasharray={isPending ? '4,4' : undefined}
              markerEnd="url(#arrowhead)"
            />
            <text x={mx} y={my - 4} textAnchor="middle" fontSize={10} fill="#6b7280">{r.relationship_type}</text>
          </g>
        );
      })}
    </svg>
  );
}

function AddEntityDialog({ onAdd, onClose }: {
  onAdd: (e: { entity_type: EntityType; label: string; confidence: number; source: string; control: FounderControl; is_pending: boolean }) => void;
  onClose: () => void;
}) {
  const [type, setType] = useState<EntityType>('Competitor');
  const [label, setLabel] = useState('');
  const [source, setSource] = useState('Founder');
  const [confidence, setConfidence] = useState(0.8);

  const submit = () => {
    if (!label.trim()) return;
    onAdd({ entity_type: type, label: label.trim(), source, confidence, control: 'none', is_pending: false });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-96 space-y-4">
        <h2 className="text-lg font-semibold text-slate-100">Add Entity</h2>
        <select value={type} onChange={e => setType(e.target.value as EntityType)}
          className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-slate-100">
          {ENTITY_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (e.g. Lindy.ai)"
          className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-slate-100" />
        <input value={source} onChange={e => setSource(e.target.value)} placeholder="Source"
          className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-slate-100" />
        <div className="flex items-center gap-3">
          <label className="text-slate-400 text-sm">Confidence:</label>
          <input type="range" min={0} max={1} step={0.05} value={confidence}
            onChange={e => setConfidence(Number(e.target.value))} className="flex-1" />
          <span className="text-slate-300 text-sm w-10">{Math.round(confidence * 100)}%</span>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-slate-200">Cancel</button>
          <button onClick={submit} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg">Add</button>
        </div>
      </div>
    </div>
  );
}

function AddRelationshipDialog({ entities, onAdd, onClose }: {
  entities: OntologyEntity[];
  onAdd: (r: { from_entity: string; to_entity: string; relationship_type: RelationshipType; strength: number }) => void;
  onClose: () => void;
}) {
  const [from, setFrom] = useState(entities[0]?.id ?? '');
  const [to, setTo] = useState(entities[1]?.id ?? entities[0]?.id ?? '');
  const [relType, setRelType] = useState<RelationshipType>('competes with');
  const [strength, setStrength] = useState(0.7);

  const submit = () => {
    if (!from || !to || from === to) return;
    onAdd({ from_entity: from, to_entity: to, relationship_type: relType, strength });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-96 space-y-4">
        <h2 className="text-lg font-semibold text-slate-100">Add Relationship</h2>
        <select value={from} onChange={e => setFrom(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-slate-100">
          {entities.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
        </select>
        <select value={relType} onChange={e => setRelType(e.target.value as RelationshipType)}
          className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-slate-100">
          {RELATIONSHIP_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <select value={to} onChange={e => setTo(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-slate-100">
          {entities.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
        </select>
        <div className="flex items-center gap-3">
          <label className="text-slate-400 text-sm">Strength:</label>
          <input type="range" min={0} max={1} step={0.05} value={strength}
            onChange={e => setStrength(Number(e.target.value))} className="flex-1" />
          <span className="text-slate-300 text-sm w-10">{Math.round(strength * 100)}%</span>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-slate-200">Cancel</button>
          <button onClick={submit} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg">Add</button>
        </div>
      </div>
    </div>
  );
}

function EntityDetailPanel({ entity, relationships, entities, onControl, onClose }: {
  entity: OntologyEntity;
  relationships: OntologyRelationship[];
  entities: OntologyEntity[];
  onControl: (c: FounderControl) => void;
  onClose: () => void;
}) {
  const entityById = useMemo(() => new Map(entities.map(e => [e.id, e])), [entities]);
  const connected = relationships.filter(r => r.from_entity === entity.id || r.to_entity === entity.id);

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-slate-400">{entity.entity_type}</div>
          <div className="text-base font-semibold text-slate-100">{entity.label}</div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-200">✕</button>
      </div>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-slate-400">Source</span><span className="text-slate-200">{entity.source}</span></div>
        <div className="flex justify-between"><span className="text-slate-400">Confidence</span><span className="text-slate-200">{Math.round(entity.confidence * 100)}%</span></div>
        <div className="flex justify-between"><span className="text-slate-400">Status</span><span className={entity.is_pending ? 'text-amber-400' : 'text-green-400'}>{entity.is_pending ? 'Pending' : 'Confirmed'}</span></div>
      </div>
      <div>
        <div className="text-xs text-slate-400 mb-1">Founder Control</div>
        <div className="flex gap-2">
          {(['locked', 'watched', 'muted', 'none'] as FounderControl[]).map(c => (
            <button key={c} onClick={() => onControl(c)}
              className={`px-2 py-1 rounded text-xs font-medium ${entity.control === c ? 'bg-cyan-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}>
              {c}
            </button>
          ))}
        </div>
        <div className="text-xs text-slate-500 mt-1">
          {entity.control === 'locked' && 'Blocks all automated updates. Changes require your confirmation.'}
          {entity.control === 'watched' && 'You get a Challenge card before any automated change is applied.'}
          {entity.control === 'muted' && 'Suppresses all automated signals about this entity.'}
          {entity.control === 'none' && 'Auto-update enabled. FYI in newsfeed for changes.'}
        </div>
      </div>
      {connected.length > 0 && (
        <div>
          <div className="text-xs text-slate-400 mb-1">Relationships</div>
          <div className="space-y-1">
            {connected.map(r => {
              const other = r.from_entity === entity.id ? r.to_entity : r.from_entity;
              const otherE = entityById.get(other);
              const dir = r.from_entity === entity.id ? '→' : '←';
              return (
                <div key={r.id} className="text-xs text-slate-300">
                  {dir} <span className="text-slate-500">{r.relationship_type}</span> {otherE?.label ?? other}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function GraphCanvas({ entities, relationships, selected, onSelect, onControl, onDelete }: {
  entities: OntologyEntity[];
  relationships: OntologyRelationship[];
  selected: OntologyEntity | undefined;
  onSelect: (e: OntologyEntity | undefined) => void;
  onControl: (id: string, c: FounderControl) => void;
  onDelete: (id: string) => void;
}) {
  const positions = useForceSimulation(entities, relationships);

  return (
    <div
      className="relative border border-slate-700 rounded-xl bg-slate-950 overflow-hidden"
      style={{ width: CANVAS_W, height: CANVAS_H }}
      onClick={() => onSelect(undefined)}
    >
      <EdgeLayer entities={entities} relationships={relationships} positions={positions} />
      {entities.map(e => {
        const p = positions.get(e.id);
        if (!p) return null;
        return (
          <EntityCardNode
            key={e.id}
            entity={e}
            x={p.x} y={p.y}
            selected={selected?.id === e.id}
            onSelect={() => { onSelect(e); }}
            onControl={c => onControl(e.id, c)}
            onDelete={() => onDelete(e.id)}
          />
        );
      })}
    </div>
  );
}

// — main view —

export const KnowledgeRoomView: React.FC = () => {
  const {
    knowledgeEntities, knowledgeRelationships, knowledgeAudit,
    selectedKnowledgeEntity,
    loadKnowledgeEntities, loadKnowledgeRelationships, loadKnowledgeAudit,
    createEntity, deleteEntity, setEntityControl, createRelationship, deleteRelationship,
    selectKnowledgeEntity,
  } = usePortalStore();

  const [showAddEntity, setShowAddEntity] = useState(false);
  const [showAddRel, setShowAddRel] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadKnowledgeEntities();
    loadKnowledgeRelationships();
  }, []);

  const handleAddEntity = async (e: Parameters<typeof createEntity>[0]) => {
    try { await createEntity(e); } catch (err) { setError(String(err)); }
  };

  const handleAddRel = async (r: Parameters<typeof createRelationship>[0]) => {
    try { await createRelationship(r); } catch (err) { setError(String(err)); }
  };

  const handleDeleteEntity = async (id: string) => {
    try { await deleteEntity(id); if (selectedKnowledgeEntity?.id === id) selectKnowledgeEntity(undefined); }
    catch (err) { setError(String(err)); }
  };

  const handleControl = async (id: string, c: FounderControl) => {
    try { await setEntityControl(id, c); } catch (err) { setError(String(err)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">Knowledge Room</h1>
          <p className="text-slate-400 mt-1 text-sm">
            {knowledgeEntities.length} entities — {knowledgeRelationships.length} relationships
            <span className="ml-3 text-amber-400">{knowledgeEntities.filter(e => e.is_pending).length} pending</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setShowAudit(!showAudit); if (!showAudit) loadKnowledgeAudit(); }}
            className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm">
            <BookOpen className="w-4 h-4" /> Audit
          </button>
          <button onClick={() => setShowAddRel(true)}
            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm">
            <Link className="w-4 h-4" /> Relationship
          </button>
          <button onClick={() => setShowAddEntity(true)}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium">
            <Plus className="w-4 h-4" /> Entity
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900 border border-red-700 rounded-lg px-4 py-2 text-red-200 text-sm flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="flex gap-4 items-start">
        <div className="flex-shrink-0 overflow-auto">
          <GraphCanvas
            entities={knowledgeEntities}
            relationships={knowledgeRelationships}
            selected={selectedKnowledgeEntity}
            onSelect={selectKnowledgeEntity}
            onControl={handleControl}
            onDelete={handleDeleteEntity}
          />
        </div>

        <div className="flex-1 min-w-0 space-y-3">
          {selectedKnowledgeEntity && (
            <EntityDetailPanel
              entity={selectedKnowledgeEntity}
              relationships={knowledgeRelationships}
              entities={knowledgeEntities}
              onControl={c => handleControl(selectedKnowledgeEntity.id, c)}
              onClose={() => selectKnowledgeEntity(undefined)}
            />
          )}

          {showAudit && (
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-2">Audit Trail</h3>
              {knowledgeAudit.length === 0
                ? <p className="text-slate-500 text-sm">No entries yet.</p>
                : knowledgeAudit.slice(0, 20).map(a => (
                  <div key={a.id} className="text-xs text-slate-400 py-1 border-b border-slate-800">
                    <span className="text-slate-300">{a.action}</span> — {a.entity_id.slice(0, 8)}
                    <span className="text-slate-500 ml-2">{new Date(a.created_at).toLocaleTimeString()}</span>
                  </div>
                ))
              }
            </div>
          )}

          <div className="bg-slate-900 border border-slate-700 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-slate-200 mb-3">Governance</h3>
            <div className="space-y-2 text-xs text-slate-400">
              <div className="flex items-start gap-2"><span className="text-green-400 font-mono">auto</span> New instance / property update — applied + FYI in newsfeed</div>
              <div className="flex items-start gap-2"><span className="text-yellow-400 font-mono">propose</span> Corrections &amp; new axioms — Challenge card, you confirm</div>
              <div className="flex items-start gap-2"><span className="text-red-400 font-mono">locked</span> Locked entity — held in queue, never auto-applied</div>
              <div className="flex items-start gap-2"><span className="text-orange-400 font-mono">watched</span> Watched entity — Challenge card before any change</div>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-700 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-slate-200 mb-2">Entity Types</h3>
            <div className="flex flex-wrap gap-1.5">
              {ENTITY_TYPES.map(t => {
                const count = knowledgeEntities.filter(e => e.entity_type === t).length;
                return (
                  <span key={t} className={`px-2 py-0.5 rounded-full text-xs border ${TYPE_COLORS[t] ?? 'border-slate-600 bg-slate-800'}`}>
                    {t} {count > 0 && <span className="font-bold">{count}</span>}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {showAddEntity && <AddEntityDialog onAdd={handleAddEntity} onClose={() => setShowAddEntity(false)} />}
      {showAddRel && knowledgeEntities.length >= 2 && (
        <AddRelationshipDialog entities={knowledgeEntities} onAdd={handleAddRel} onClose={() => setShowAddRel(false)} />
      )}
    </div>
  );
};
